import { getPageForApi } from 'utils/factory';
import { EN } from 'lib/locales';

import { authHeaders, betterFetch, CCS_URL } from './helper';

/**
 * add a new page to the canvas backend
 * @param {object} pagePayload - page payload object
 * @returns {Promise<Response>} the response object
 */
export const addPage = function addPage(pagePayload) {
  const headers = authHeaders();
  const payload = getPageForApi(pagePayload);
  const options = {
    method: 'POST',
    headers,
    body: JSON.stringify(payload)
  };

  return betterFetch(`${CCS_URL}/page`, options);
};

/**
 * deletes page data from dynamo
 * @param {string} pageId - the id of the page
 * @returns {Promise<Response>} the response object
 */
export const deletePage = function deletePage(pageId) {
  const headers = authHeaders();
  const options = {
    method: 'DELETE',
    headers
  };
  const url = `${CCS_URL}/page/${pageId}`;
  return betterFetch(url, options);
};

/**
 * get an existing page from the canvas backend
 * @param {object} params - function params
 * @param {string} params.id - page uuid
 * @param {string} params.version - version number
 * @param {string} [params.locale = EN] - the locale of the page
 * @returns {Promise<Response>} the response object
 */
export const getPage = async function getPage({ id, version, locale = EN }) {
  const headers = authHeaders();
  const queryParams = new URLSearchParams({
    version,
    locale
  });
  const url = `${CCS_URL}/page/${id}?${queryParams}`;

  const response = await betterFetch(url, { headers });
  return response;
};

/**
 * make a publish page request
 * @param {object} pagePayload - the page to be published
 * @param {boolean} publishNow -  for publish now true/false.
 * @param {string} dateTime - dateTime of the scheduled publish
 * @param {boolean} isTestContent - whether content is published for testing purposes only
 * @returns {Promise<Response>} the response object
 */
export const publishPage = function publishPage(
  pagePayload,
  publishNow,
  dateTime,
  isTestContent = false
) {
  if (!pagePayload) {
    return Promise.reject(new Error('Page can not null.'));
  }

  if (!pagePayload.Id) {
    return Promise.reject(new Error('Page id can not null.'));
  }
  if (!pagePayload.Version) {
    return Promise.reject(new Error('Page version can not null.'));
  }
  if (!publishNow && !dateTime) {
    return Promise.reject(
      new Error('A valid date-time should be present when publish now is false.')
    );
  }

  const headers = authHeaders();
  const options = {
    method: 'POST',
    headers
  };
  const queryParams = new URLSearchParams({
    version: pagePayload.Version,
    locale: pagePayload.Locale,
    publishNow,
    startDate: dateTime,
    lastTimestamp: pagePayload.LastTimestamp,
    isTestContent
  });

  const url = `${CCS_URL}/page/publish/${pagePayload.Id}?${queryParams}`;

  return betterFetch(url, options);
};

/**
 * make a unpublish page request
 * @param {object} pagePayload - the page payload to be unpublished
 * @returns {Promise<Response>} the response object
 */
export const unpublishPage = function publishPage(pagePayload) {
  if (!pagePayload) {
    return Promise.reject(new Error('Page can not null.'));
  }
  if (!pagePayload.Id) {
    return Promise.reject(new Error('Page id can not null.'));
  }

  const headers = authHeaders();
  const options = {
    method: 'POST',
    headers
  };
  const queryParams = new URLSearchParams({
    locale: pagePayload.Locale,
    lastTimestamp: pagePayload.LastTimestamp
  });
  const url = `${CCS_URL}/page/unpublish/${pagePayload.Id}?${queryParams}`;

  return betterFetch(url, options);
};

/**
 * saves the modifications done on the page
 * @param {object} pagePayload - the page to be saved
 * @returns {Promise<Response>} the response object
 */
export const savePage = function savePage(pagePayload) {
  if (!pagePayload) {
    return Promise.reject(new Error('Page can not null.'));
  }
  if (!pagePayload.Id) {
    return Promise.reject(new Error('Page id can not null.'));
  }
  if (!pagePayload.Version) {
    return Promise.reject(new Error('Page version can not null.'));
  }

  const headers = authHeaders();
  const payload = getPageForApi(pagePayload);

  const options = {
    method: 'PUT',
    headers,
    body: JSON.stringify(payload)
  };

  return betterFetch(`${CCS_URL}/page`, options);
};

/**
 * moves a page to a different folder
 * @param {string} pageId - page id (to be moved)
 * @param {string} targetFolderId - target folder id
 * @returns {Promise<Response>} the response object
 */
export const movePage = function movePage(pageId, targetFolderId) {
  const headers = authHeaders();
  const options = {
    method: 'PUT',
    headers
  };
  const queryParams = new URLSearchParams({
    newFolderId: targetFolderId
  });
  const url = `${CCS_URL}/page/move/${pageId}?${queryParams}`;

  return betterFetch(url, options);
};

/**
 * get data for published pages report
 *
 * @function
 * @param {object} params - function params
 * @param {string} params.endDate - end date for the report
 * @param {string} params.startDate - start date for the report
 * @returns {Promise.<object[]>} response data
 */
export const getPublishedPages = function getPublishedPages({ endDate, startDate }) {
  const headers = authHeaders();
  const queryParams = new URLSearchParams({ startDate, endDate });
  const url = `${CCS_URL}/misc/live?${queryParams}`;

  return betterFetch(url, { headers });
};
